import { useEffect, useState } from "react";
import { Flame, Check } from "lucide-react";
import { DailyScoreChart } from "@/components/DailyScoreChart";
import { api } from "@/lib/api";
import { LevelBadge } from "@/components/LevelBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { BadgeCard } from "@/components/BadgeCard";
import { TeamLink } from "@/components/TeamLink";
import { DictionCard } from "@/components/DictionCard";

const day = (d) => new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "narrow" });
const num = (d) => new Date(d + "T12:00:00").getDate();

export default function ProgressPage() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/progress").then((r) => setP(r.data)); }, []);
  if (!p) return <Skeleton className="h-64 w-full" />;
  const week = p.days.slice(-7);

  return (
    <div className="space-y-6">
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase">Progression</h1>

      <div className="rounded-2xl bg-slate-900 text-white p-6 flex items-center justify-between rise">
        <div>
          <p className="text-xs font-bold uppercase text-slate-400">Série en cours</p>
          <p className="font-display text-6xl font-extrabold flex items-center gap-2" data-testid="progress-streak">
            <Flame className="w-12 h-12 text-orange-500" /> {p.streak}
          </p>
          <p className="text-slate-300 text-sm">jour{p.streak > 1 ? "s" : ""} d'affilée</p>
        </div>
        <div className="text-right space-y-2">
          <p className="text-xs font-bold uppercase text-slate-400">Niveau</p>
          <LevelBadge level={p.level} testId="progress-level-badge" />
        </div>
      </div>

      <TeamLink testId="progress-team-link" />
      <DictionCard />

      <section className="bg-white rounded-2xl border-2 border-slate-200 p-5 rise rise-1">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold uppercase text-slate-500">Badges</p>
          <p className="text-xs font-bold text-slate-500" data-testid="progress-best-streak">Record : {p.best_streak} j</p>
        </div>
        <div className="grid grid-cols-3 gap-2" data-testid="progress-badges">
          {p.badges.map((b) => <BadgeCard key={b.id} badge={b} />)}
        </div>
      </section>

      <section className="bg-white rounded-2xl border-2 border-slate-200 p-5 rise rise-1">
        <p className="text-xs font-bold uppercase text-slate-500 mb-3">Cette semaine</p>
        <div className="grid grid-cols-7 gap-2" data-testid="progress-week">
          {week.map((d) => (
            <div key={d.date} className="flex flex-col items-center gap-1" data-testid={`progress-day-${d.date}`}>
              <span className="text-xs font-bold uppercase text-slate-500">{day(d.date)}</span>
              <span className={`w-10 h-10 rounded-full grid place-items-center font-bold ${d.lessons ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                {d.lessons ? <Check className="w-5 h-5" /> : num(d.date)}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-2xl border-2 border-slate-200 p-5 rise rise-2" data-testid="progress-chart">
        <p className="text-xs font-bold uppercase text-slate-500 mb-3">Réussite par jour (14 jours)</p>
        <DailyScoreChart days={p.days} />
      </section>

      <section className="grid grid-cols-3 gap-3 rise rise-3">
        {[["Leçons", p.lessons_completed, "progress-stat-lessons"], ["Moyenne", `${p.average}%`, "progress-stat-average"], ["Jours actifs", p.days_active, "progress-stat-days"]].map(([l, v, id]) => (
          <div key={l} className="bg-white rounded-xl border-2 border-slate-200 p-4">
            <div className="font-display text-3xl font-bold" data-testid={id}>{v}</div>
            <div className="text-xs font-bold uppercase text-slate-500">{l}</div>
          </div>
        ))}
      </section>
    </div>
  );
}
